<script lang="ts">
  import { Link, router } from '@inertiajs/svelte';
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import type { NowItem, NowDensity } from '../lib/types';
  import { NOW_DENSITIES } from '../lib/types';
  import {
    nowState,
    nowDensity,
    removeFromNow,
    completeNowItem,
    saveNowPreferences,
    refreshNowQuietly,
    setNowDensity,
  } from '../lib/stores/now';
  import { stayTone } from '../lib/nowStay';
  import NowTaskDetailModal from './NowTaskDetailModal.svelte';
  import NowActionConfirmModal from './NowActionConfirmModal.svelte';
  import NowAddPicker from './NowAddPicker.svelte';

  const DENSITY_LABELS: Record<NowDensity, string> = {
    compact: 'Compact',
    normal: 'Normal',
    large: 'Large',
    hidden: 'Hidden',
  };

  let pickerOpen = $state(false);
  let pickerAnchor: HTMLElement | null = $state(null);

  let settingsOpen = $state(false);
  let settingsAnchor: HTMLElement | null = $state(null);
  let settingsEl: HTMLDivElement | undefined = $state();
  let settingsStyle = $state('position: fixed; visibility: hidden;');

  let currentNow = $derived($nowState);
  let density = $derived($nowDensity === 'hidden' ? 'compact' : $nowDensity);
  let emptySlotCount = $derived(
    Math.max(
      0,
      currentNow.capacity -
        currentNow.items.length -
        (currentNow.refillSameDay ? 0 : currentNow.completedToday.length),
    ),
  );

  // eslint-disable-next-line momentium/no-unguarded-effect -- refresh Now when the tab becomes visible again (day rollover)
  $effect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') refreshNowQuietly();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  });

  async function positionPopover(anchor: HTMLElement, floating: HTMLElement) {
    const result = await computePosition(anchor, floating, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(6),
        flip({
          fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
        }),
        shift({ padding: 8 }),
      ],
    });
    return `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate for settings
  $effect(() => {
    if (!settingsOpen || !settingsAnchor || !settingsEl) return;
    const anchor = settingsAnchor;
    const floating = settingsEl;
    return autoUpdate(anchor, floating, async () => {
      settingsStyle = await positionPopover(anchor, floating);
    });
  });

  function closePicker() {
    pickerOpen = false;
    pickerAnchor = null;
  }

  function closeSettings() {
    settingsOpen = false;
    settingsAnchor = null;
    settingsStyle = 'position: fixed; visibility: hidden;';
  }

  function openPickerFrom(el: HTMLElement) {
    if (!currentNow.canAdd) return;
    if (pickerOpen && pickerAnchor === el) {
      closePicker();
      return;
    }
    closeSettings();
    pickerAnchor = el;
    pickerOpen = true;
  }

  function toggleSettings(el: HTMLElement) {
    if (settingsOpen && settingsAnchor === el) {
      closeSettings();
      return;
    }
    closePicker();
    settingsAnchor = el;
    settingsOpen = true;
  }

  // NowAddPicker handles its own Esc and outside clicks.
  // eslint-disable-next-line momentium/no-unguarded-effect -- document listeners while the settings popover is open
  $effect(() => {
    if (!settingsOpen) return;

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSettings();
      }
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (settingsEl?.contains(target) || settingsAnchor?.contains(target)) return;
      closeSettings();
    }

    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  function stayLabel(days: number): string {
    return days === 1 ? '1d' : `${days}d`;
  }

  let taskDetail: NowTaskDetailModal | undefined = $state();
  let pendingAction = $state<null | { type: 'complete' | 'remove'; item: NowItem }>(null);

  function chipTooltip(item: NowItem): string {
    return `${item.title} (Project: "${item.projectTitle}")`;
  }

  function openNowTask(item: NowItem) {
    void taskDetail?.open(item);
  }

  function requestChipAction(type: 'complete' | 'remove', item: NowItem) {
    pendingAction = { type, item };
  }

  async function confirmChipAction() {
    const pending = pendingAction;
    pendingAction = null;
    if (!pending) return;
    if (pending.type === 'complete') {
      await completeNowItem(pending.item.taskId, pending.item.projectId);
      return;
    }
    await removeFromNow(pending.item.taskId);
  }

  function goToSettings() {
    closeSettings();
    router.get('/settings');
  }
</script>

<section class="now-bar" data-density={density} aria-label="Now, daily commitments">
  <div class="now-label">
    <span class="now-kicker">Now</span>
    <span class="now-count">{currentNow.occupied}/{currentNow.capacity}</span>
  </div>

  <div class="now-slots">
    {#each currentNow.items as item, index (item.taskId)}
      <article class="now-chip" class:up-next={index === 0}>
        {#if index === 0}
          <span class="up-next-label">Up next</span>
        {/if}
        <button
          type="button"
          class="chip-title"
          onclick={() => openNowTask(item)}
          title={chipTooltip(item)}
        >
          {item.title}
        </button>
        <span class="stay-badge {stayTone(item.currentStayDays)}" title="Days in Now this stay">
          {stayLabel(item.currentStayDays)}
        </span>
        <button
          type="button"
          class="chip-complete"
          title="Mark done"
          onclick={() => requestChipAction('complete', item)}
        >
          ✓
        </button>
        <button
          type="button"
          class="chip-remove"
          title="Remove from Now"
          onclick={() => requestChipAction('remove', item)}
        >
          ×
        </button>
      </article>
    {/each}

    {#each currentNow.completedToday as item (item.taskId)}
      <article class="now-chip done">
        <button
          type="button"
          class="chip-title done-title"
          onclick={() => openNowTask(item)}
          title={chipTooltip(item)}
        >
          {item.title}
        </button>
        <span class="stay-badge fresh">{stayLabel(item.currentStayDays)}</span>
        <span class="done-flag">Done</span>
      </article>
    {/each}

    {#each Array(emptySlotCount) as _, i (i)}
      <button type="button" class="empty-slot" onclick={(e) => openPickerFrom(e.currentTarget)}>
        + Add
      </button>
    {/each}
  </div>

  <div class="now-actions">
    <Link href="/now/board" class="icon-btn" title="Open Now board" aria-label="Open Now board">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M9.25 3H13v3.75M13 3 8.75 7.25M6.75 13H3V9.25M3 13l4.25-4.25"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </Link>
    <button
      type="button"
      class="icon-btn"
      title="Now settings"
      aria-label="Now settings"
      onclick={(e) => toggleSettings(e.currentTarget)}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
          stroke="currentColor"
          stroke-width="2"
          stroke-linejoin="round"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
      </svg>
    </button>
  </div>
</section>

<NowAddPicker open={pickerOpen} anchor={pickerAnchor} onClose={closePicker} />

{#if settingsOpen}
  <div
    class="popover settings"
    bind:this={settingsEl}
    style={settingsStyle}
    onmousedown={(e) => e.stopPropagation()}
    role="dialog"
    tabindex="-1"
    aria-label="Now settings"
  >
    <div class="popover-header">
      <strong>Now settings</strong>
      <button type="button" class="icon-btn" onclick={closeSettings} aria-label="Close">×</button>
    </div>
    <div class="setting-row">
      <span>Size</span>
      <div class="density-pills" role="radiogroup" aria-label="Now size">
        {#each NOW_DENSITIES as option}
          <button
            type="button"
            class="density-pill"
            class:active={$nowDensity === option}
            onclick={() => setNowDensity(option)}
          >
            {DENSITY_LABELS[option]}
          </button>
        {/each}
      </div>
    </div>
    <label class="setting-row">
      <span>Capacity</span>
      <input
        autocomplete="off"
        type="number"
        min="1"
        max="7"
        value={currentNow.capacity}
        onchange={(e) => saveNowPreferences({ capacity: Number(e.currentTarget.value) })}
      />
    </label>
    <label class="setting-row">
      <span>Refill slots the same day</span>
      <input
        type="checkbox"
        checked={currentNow.refillSameDay}
        onchange={(e) => saveNowPreferences({ refillSameDay: e.currentTarget.checked })}
      />
    </label>
    <p class="setting-hint">
      Default is locked for the day: finishing a task keeps the slot until tomorrow. Hidden is the
      default for new visitors — press N to show Now again.
    </p>
    <button type="button" class="settings-link" onclick={goToSettings}>Open Settings</button>
  </div>
{/if}

<NowTaskDetailModal bind:this={taskDetail} />

<NowActionConfirmModal
  open={pendingAction !== null}
  action={pendingAction?.type ?? 'remove'}
  taskTitle={pendingAction?.item.title ?? ''}
  refillSameDay={currentNow.refillSameDay}
  onCancel={() => (pendingAction = null)}
  onConfirm={confirmChipAction}
/>

<style>
  .now-bar {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.375rem 0.75rem;
    background: #fffbeb;
    border-bottom: 1px solid #fde68a;
    min-height: 2.5rem;
    flex-shrink: 0;
    z-index: 40;
  }

  .now-bar[data-density='normal'] {
    min-height: 5rem;
    padding: 0.75rem 1rem;
    gap: 1rem;
  }

  .now-bar[data-density='large'] {
    min-height: 7rem;
    padding: 1rem 1.25rem;
    gap: 1.15rem;
  }

  .now-label {
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
    flex-shrink: 0;
  }

  .now-kicker {
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
  }

  .now-bar[data-density='normal'] .now-kicker {
    font-size: 0.875rem;
  }

  .now-bar[data-density='large'] .now-kicker {
    font-size: 1.05rem;
  }

  .now-count {
    font-size: 0.6875rem;
    color: #92400e;
    font-weight: 600;
  }

  .now-bar[data-density='normal'] .now-count,
  .now-bar[data-density='large'] .now-count {
    font-size: 0.8125rem;
  }

  .now-slots {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
  }

  .now-bar[data-density='normal'] .now-slots,
  .now-bar[data-density='large'] .now-slots {
    gap: 0.6rem;
  }

  .now-chip,
  .empty-slot {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border-radius: 999px;
    padding: 0.2rem 0.4rem 0.2rem 0.45rem;
    font-size: 0.75rem;
    line-height: 1.2;
    flex-shrink: 0;
  }

  .now-bar[data-density='normal'] .now-chip,
  .now-bar[data-density='normal'] .empty-slot {
    font-size: 1rem;
    padding: 0.4rem 0.7rem;
    gap: 0.45rem;
  }

  .now-bar[data-density='large'] .now-chip,
  .now-bar[data-density='large'] .empty-slot {
    font-size: 1.25rem;
    padding: 0.55rem 0.9rem;
    gap: 0.55rem;
  }

  .now-chip {
    background: white;
    border: 1px solid #f3e8c8;
    box-shadow: 0 1px 1px rgba(180, 83, 9, 0.05);
  }

  .now-chip.done {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }

  .chip-title {
    max-width: 12rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    color: #1f2937;
    cursor: pointer;
  }

  .now-bar[data-density='normal'] .chip-title {
    max-width: 18rem;
  }

  .now-bar[data-density='large'] .chip-title {
    max-width: 24rem;
  }

  .done-title {
    color: #166534;
    text-decoration: line-through;
  }

  /* First active chip: what to work on next (same order as the board). */
  .now-chip.up-next {
    border-color: #fbbf24;
    box-shadow: 0 1px 2px rgba(180, 83, 9, 0.12);
  }

  .up-next-label {
    font-size: 0.5625rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .now-bar[data-density='normal'] .up-next-label {
    font-size: 0.6875rem;
  }

  .now-bar[data-density='large'] .up-next-label {
    font-size: 0.8125rem;
  }

  .stay-badge {
    font-size: 0.625rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.05rem 0.3rem;
  }

  .now-bar[data-density='normal'] .stay-badge {
    font-size: 0.75rem;
  }

  .now-bar[data-density='large'] .stay-badge {
    font-size: 0.875rem;
  }

  .stay-badge.fresh {
    background: #f3f4f6;
    color: #6b7280;
  }

  .stay-badge.lingering {
    background: #fef3c7;
    color: #b45309;
  }

  .stay-badge.stale {
    background: #fee2e2;
    color: #b91c1c;
  }

  .chip-complete,
  .chip-remove,
  .icon-btn {
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 999px;
    width: 1.1rem;
    height: 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 0.75rem;
    text-decoration: none;
    flex-shrink: 0;
  }

  .now-bar[data-density='normal'] .chip-complete,
  .now-bar[data-density='normal'] .chip-remove,
  .now-bar[data-density='normal'] .icon-btn {
    width: 1.4rem;
    height: 1.4rem;
    font-size: 0.9rem;
  }

  .now-bar[data-density='large'] .chip-complete,
  .now-bar[data-density='large'] .chip-remove,
  .now-bar[data-density='large'] .icon-btn {
    width: 1.65rem;
    height: 1.65rem;
    font-size: 1.05rem;
  }

  .chip-complete:hover {
    color: #15803d;
    background: #dcfce7;
  }

  .chip-remove:hover,
  .icon-btn:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .done-flag {
    font-size: 0.625rem;
    font-weight: 700;
    color: #15803d;
  }

  .now-bar[data-density='normal'] .done-flag {
    font-size: 0.75rem;
  }

  .now-bar[data-density='large'] .done-flag {
    font-size: 0.875rem;
  }

  .empty-slot {
    border: 1px dashed #d6b56a;
    background: transparent;
    color: #92400e;
    cursor: pointer;
    min-width: 4.25rem;
    justify-content: center;
  }

  .empty-slot:hover {
    background: #fff7d6;
  }

  .now-actions {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    flex-shrink: 0;
  }

  /* Link is a child component, so these must be :global to match the settings button. */
  .now-actions :global(.icon-btn) {
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 999px;
    width: 1.65rem;
    height: 1.65rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 0;
    text-decoration: none;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .now-actions :global(.icon-btn svg) {
    display: block;
    width: 0.95rem;
    height: 0.95rem;
  }

  .now-bar[data-density='normal'] .now-actions :global(.icon-btn) {
    width: 1.9rem;
    height: 1.9rem;
  }

  .now-bar[data-density='normal'] .now-actions :global(.icon-btn svg) {
    width: 1.1rem;
    height: 1.1rem;
  }

  .now-bar[data-density='large'] .now-actions :global(.icon-btn) {
    width: 2.15rem;
    height: 2.15rem;
  }

  .now-bar[data-density='large'] .now-actions :global(.icon-btn svg) {
    width: 1.25rem;
    height: 1.25rem;
  }

  .now-actions :global(.icon-btn:hover) {
    color: #374151;
    background: #f3f4f6;
  }

  .popover {
    width: 22rem;
    max-width: calc(100vw - 1rem);
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
    z-index: 80;
    padding: 0.625rem;
  }

  .popover-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
  }

  .setting-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.25rem 0;
  }

  .setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.8125rem;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .setting-row input[type='number'] {
    width: 4rem;
    font: inherit;
    padding: 0.2rem 0.35rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
  }

  .density-pills {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.25rem;
  }

  .density-pill {
    border: 1px solid #d1d5db;
    background: #fff;
    color: #4b5563;
    border-radius: 999px;
    padding: 0.15rem 0.45rem;
    font: inherit;
    font-size: 0.6875rem;
    cursor: pointer;
  }

  .density-pill.active {
    background: #fffbeb;
    border-color: #f59e0b;
    color: #92400e;
    font-weight: 600;
  }

  .settings-link {
    border: none;
    background: none;
    color: #2563eb;
    font: inherit;
    font-size: 0.75rem;
    padding: 0;
    cursor: pointer;
  }
</style>
